/**
 * Notifications on THIS device.
 *
 * Deliberately not a config field. `p.push` is a preference — whether this
 * person wants paging at all — and it follows them everywhere. Whether a
 * particular browser on a particular machine holds a push subscription is a
 * property of the device, and the two are genuinely different: somebody can
 * want pages and simply not have registered their laptop yet.
 *
 * Every failure says which of the three things went wrong, because they are
 * fixed in three different places: the browser cannot do it, the deployment
 * has no keys, or the person said no.
 */
import { useEffect, useState } from 'react'
import { Button } from '../ui'
import { push, type PushState } from '../../lib/push'

export function PushDeviceRow() {
  const [state, setState] = useState<PushState | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    push
      .current()
      .then((s) => {
        if (!cancelled) setState(s)
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'unsupported', reason: 'Notifications could not be checked in this browser.' })
      })
    return () => {
      cancelled = true
    }
  }, [])

  async function run(fn: () => Promise<PushState>) {
    setBusy(true)
    try {
      setState(await fn())
    } catch (err) {
      setState({
        status: 'not-configured',
        reason: err instanceof Error ? err.message : 'Notifications could not be changed on this device.',
      })
    } finally {
      setBusy(false)
    }
  }

  const on = state?.status === 'on'
  const blocked = state?.status === 'unsupported' || state?.status === 'denied'

  return (
    <div
      data-testid="push-device-row"
      className="flex items-start justify-between gap-4 border-b border-subtle py-3"
    >
      <div className="min-w-0">
        <div className="text-[13px] text-hi">Notifications on this device</div>
        <p className="mt-0.5 max-w-[420px] text-[12px] leading-[1.5] text-lo">
          {state === null
            ? 'Checking…'
            : state.status === 'on'
              ? 'This device is registered. It will buzz for conversations you are paged about — never carrying the message text.'
              : state.status === 'off'
                ? 'This device is not registered, so nothing will reach it when you are away from the console.'
                : state.reason}
        </p>
      </div>

      {!blocked && (
        <Button
          variant={on ? 'secondary' : 'primary'}
          data-testid="push-device-toggle"
          disabled={busy || state === null}
          onClick={() => void run(on ? push.disable : push.enable)}
        >
          {busy ? 'Working…' : on ? 'Turn off here' : 'Turn on here'}
        </Button>
      )}
    </div>
  )
}
